import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { LayoutDashboard, User, MapPin, Heart, Package, Shield, Lock, LogOut, Loader2, Gift, LifeBuoy, Trophy } from "lucide-react";
import Footer from "../components/Footer";
import { useAuth } from "../context/AuthContext";
import {
  Overview, ProfileSection, AddressesSection, WishlistSection,
  OrdersSection, SecuritySection, PrivacySection,
  RewardsSection, SupportSection,
} from "../components/account/AccountSections";
import { PartnershipSection } from "../components/account/PartnershipSection";

const NAV = [
  { id: "overview", label: "Dashboard", icon: LayoutDashboard },
  { id: "profile", label: "Profile", icon: User },
  { id: "addresses", label: "Addresses", icon: MapPin },
  { id: "wishlist", label: "Wishlist", icon: Heart },
  { id: "orders", label: "Orders", icon: Package },
  { id: "rewards", label: "Rewards", icon: Gift },
  { id: "partnership", label: "Partnership", icon: Trophy },
  { id: "support", label: "Support", icon: LifeBuoy },
  { id: "security", label: "Security", icon: Shield },
  { id: "privacy", label: "Privacy", icon: Lock },
];

export default function Account() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [tab, setTabState] = useState(params.get("tab") || "overview");

  useEffect(() => { if (user === false) navigate("/login", { replace: true }); }, [user, navigate]);

  const setTab = (t) => { setTabState(t); setParams({ tab: t }); window.scrollTo(0, 0); };

  if (!user || !user.user_id) {
    return <div className="min-h-screen flex items-center justify-center bg-[#0F0F10]"><Loader2 className="h-6 w-6 animate-spin text-[#4ADE80]" /></div>;
  }

  const doLogout = async () => { await logout(); navigate("/", { replace: true }); };

  const render = () => {
    switch (tab) {
      case "profile": return <ProfileSection />;
      case "addresses": return <AddressesSection />;
      case "wishlist": return <WishlistSection />;
      case "orders": return <OrdersSection />;
      case "rewards": return <RewardsSection />;
      case "partnership": return <PartnershipSection />;
      case "support": return <SupportSection user={user} />;
      case "security": return <SecuritySection user={user} />;
      case "privacy": return <PrivacySection />;
      default: return <Overview user={user} setTab={setTab} />;
    }
  };

  return (
    <div className="pt-16 min-h-screen flex flex-col bg-[#0F0F10]">
      <div className="mx-auto max-w-[1400px] w-full px-5 sm:px-8 py-10 flex-1 grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-10">
        {/* Sidebar */}
        <aside className="lg:sticky lg:top-24 lg:self-start">
          <div className="border border-white/10 bg-[#1F1F1F] rounded-2xl p-5">
            <div className="flex items-center gap-3 pb-4 border-b border-white/10">
              <div className="h-10 w-10 rounded-full bg-[#4ADE80] text-[#0F0F10] flex items-center justify-center font-display font-extrabold">{(user.name || user.email)[0]?.toUpperCase()}</div>
              <div className="min-w-0"><p className="font-display font-bold tracking-tight truncate">{user.name}</p><p data-testid="account-email" className="text-[11px] text-[#7a7a7a] truncate">{user.email}</p></div>
            </div>
            <nav className="mt-4 flex lg:flex-col gap-1 overflow-x-auto lg:overflow-visible" data-testid="account-nav">
              {NAV.map((n) => (
                <button key={n.id} data-testid={`nav-${n.id}`} onClick={() => setTab(n.id)}
                  className={`flex items-center gap-3 px-4 py-3 rounded-xl text-[13px] font-medium whitespace-nowrap transition-colors ${tab === n.id ? "bg-[#4ADE80] text-[#0F0F10]" : "text-[#A8A8A8] hover:text-[#F5F5F5] hover:bg-black/[0.04]"}`}>
                  <n.icon className="h-4 w-4" /> {n.label}
                </button>
              ))}
              <button data-testid="logout-btn" onClick={doLogout} className="flex items-center gap-3 px-4 py-3 rounded-xl text-[13px] font-medium text-[#A8A8A8] hover:text-[#EF4444] hover:bg-[#EF4444]/[0.06] transition-colors mt-2">
                <LogOut className="h-4 w-4" /> Log Out
              </button>
            </nav>
          </div>
        </aside>

        {/* Content */}
        <main className="min-w-0">{render()}</main>
      </div>
      <Footer />
      <div className="h-16 lg:hidden" aria-hidden="true" />
    </div>
  );
}
